<script setup lang="ts">
import { useSchool } from '~/composables/useSchool'
import type { SchoolCertification } from '~/composables/useSchool'

const { getCertifications } = useSchool()
const { data: certifications } = await useAsyncData('certifications', () => getCertifications())

const props = defineProps<{
    centered?: boolean
    compact?: boolean
}>()

// Each logo gets a fixed box (object-contain keeps its proportions): a lazy logo with an
// auto width would grow from zero once loaded and shift the layout. `sizes` gives the box width.
const LOGO_CLASSES_COMPACT = 'h-24 w-48 object-contain opacity-60 hover:opacity-100 transition-opacity duration-300'
const LOGO_CLASSES = 'h-24 w-48 md:h-32 md:w-64 object-contain transition-all duration-500'
const LOGO_SIZES_COMPACT = '192px'
const LOGO_SIZES = '192px md:256px'

const isModalOpen = ref(false)
const modalImage = ref('')
const modalTitle = ref('')

const openModal = (image: string, title: string) => {
    modalImage.value = image
    modalTitle.value = title
    isModalOpen.value = true
}

const closeModal = () => {
    isModalOpen.value = false
    modalImage.value = ''
    modalTitle.value = ''
}

const handleCertClick = (cert: SchoolCertification) => {
    switch (cert.clickBehavior) {
        case 1: // External Link
            if (cert.targetUrl) window.open(cert.targetUrl, '_blank')
            break
        case 2: // Zoom Logo
            openModal(cert.logo, cert.name)
            break
        case 3: // Show Certificate
            if (cert.certificateImage) openModal(cert.certificateImage, cert.name)
            break
    }
}
</script>

<template>
  <div v-if="certifications && certifications.length > 0" :class="props.compact ? 'flex flex-wrap gap-6 items-start justify-start' : props.centered ? 'flex flex-wrap gap-8 py-8 w-full justify-center' : 'flex flex-wrap gap-12 md:gap-16 py-8 w-full justify-center md:justify-between'">
    <div
      v-for="cert in certifications"
      :key="cert.id"
      :class="props.compact ? 'flex flex-col items-center' : 'flex flex-col items-center justify-start flex-1 min-w-[160px] max-w-[220px]'"
    >
      <div
        class="relative group cursor-pointer transition-all duration-300"
        :class="cert.clickBehavior !== 0 ? 'hover:scale-105' : ''"
        @click="handleCertClick(cert)"
      >
        <NuxtImg loading="lazy"
          :src="cert.logo"
          :alt="cert.name"
          :class="props.compact ? LOGO_CLASSES_COMPACT : LOGO_CLASSES"
          :sizes="props.compact ? LOGO_SIZES_COMPACT : LOGO_SIZES"
        />

        <!-- Hover indicator for interactive elements -->
        <div
            v-if="cert.clickBehavior !== 0 && !props.compact"
            class="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-oxo-red text-white flex items-center justify-center scale-0 group-hover:scale-100 transition-transform duration-300 shadow-lg"
        >
            <i v-if="cert.clickBehavior === 1" class="fas fa-external-link-alt text-[10px]" aria-hidden="true"></i>
            <i v-else class="fas fa-magnifying-glass-plus text-[10px]" aria-hidden="true"></i>
        </div>
      </div>

      <!-- Mandatory Charter Mention -->
      <p
        v-if="cert.requirementText || cert.name.toLowerCase().includes('qualiopi')"
        :class="props.compact
          ? 'mt-1 text-sm text-foreground-dim tracking-tight text-center leading-tight max-w-[160px]'
          : 'mt-4 text-xs md:text-sm font-bold tracking-tight text-center leading-tight w-full text-foreground'"
      >
        <template v-if="cert.name.toLowerCase().includes('qualiopi')">
            <span>La certification qualité a été délivrée au titre de la catégorie d'actions suivante :</span>
            <span :class="props.compact ? 'block text-foreground dark:text-oxo-yellow' : 'text-foreground dark:text-oxo-yellow block mt-1'">{{ cert.requirementText }}</span>
        </template>
        <template v-else>
            {{ cert.requirementText }}
        </template>
      </p>
    </div>

    <!-- Simple Image Modal -->
    <Teleport to="body">
      <div 
        v-if="isModalOpen" 
        class="fixed inset-0 z-1100 flex items-center justify-center p-4 md:p-8"
        @click="closeModal"
      >
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black/90 backdrop-blur-xs"></div>

        <!-- Content -->
        <div 
            class="relative z-10 max-w-5xl w-full max-h-full flex flex-col items-center"
            @click.stop
        >
          <button 
            @click="closeModal" 
            class="absolute -top-12 right-0 md:-right-12 text-white/50 hover:text-white transition-colors p-2"
          >
            <i class="fas fa-times text-2xl" aria-hidden="true"></i>
          </button>

          <div class="bg-white rounded-2xl p-2 shadow-2xl overflow-hidden">
            <img 
                :src="modalImage" 
                :alt="modalTitle" 
                class="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
          </div>
          
          <h3 class="mt-6 text-xl font-display font-bold text-white text-center">{{ modalTitle }}</h3>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
/* Ensure logos look good in white/dark modes */
img {
    max-height: 135px;
}
</style>
